/**
 * The first sentence of every call, and whether it is recorded.
 *
 * THE WORDS WAIT FOR THE BAR; A RE-RECORDING DOES NOT. The greeting used to have its own
 * Save that changed the words AND recorded them in one press. The words are a draft now,
 * saved with everything else, and the bar's save still records them - it posts to the same
 * `POST /v1/agent/greeting`, after the name and the voice, so it records once in the final
 * voice with the final name. What stays immediate is "Record it" / "Record again" on
 * unchanged words: a recording goes stale without the words changing (the business renamed,
 * a language changed), and there is nothing to save, only a recording to make.
 *
 * FIVE STATES, AND THE PILL SAYS WHICH. The prototype drew four - default, read out live,
 * recorded, out of date - and a draft adds a fifth: words typed and not yet saved, which
 * will be recorded when they are. `stale` IS NEVER COMPUTED HERE: the Worker decides whether
 * a recording matches the words and the voice, with the function the agent uses to decide
 * whether to play it.
 *
 * NO "HEAR DEFAULT". The prototype offered to play the default greeting; nothing ever records
 * it, so there is nothing to play, and the placeholder shows its words instead. No vendor is
 * named anywhere in what this renders, which is the rule `VoicePane.tsx` keeps.
 *
 * `{{business_name}}` IS THE ONE DETAIL, because the greeting is recorded once, before any
 * call, and can only know what the dashboard knows.
 */

import { useRef, useState } from "react";
import { ApiError, post, type AgentPage } from "../../../api";
import { RecordingPlayer } from "../../RecordingPlayer";
import { Button, CountTag, SettingCard, StatusPill } from "../../../ui";
import { when } from "../../../format";
import { useDraft } from "../draft";

type Greeting = AgentPage["greeting"];

/** The Worker's cap, as `maxLength` so the box stops rather than refusing afterwards. */
const MAX_GREETING_CHARS = 400;

export type GreetingState = "default" | "pending" | "live" | "recorded" | "stale";

/** Which of the five this greeting is in. Pure, so a test can walk all five. */
export function greetingState(block: Greeting, saved: string, draft: string): GreetingState {
  if (draft.trim() !== saved.trim()) return "pending";
  if (!saved.trim()) return "default";
  if (!block.audio) return "live";
  return block.audio.stale ? "stale" : "recorded";
}

export function GreetingPane({ agentId, block, onRecorded }: { agentId: string; block: Greeting; onRecorded: () => void }) {
  const { state, set, status } = useDraft();
  const text = state.draft.greeting.text;
  const saved = state.baseline.greeting.text;
  const box = useRef<HTMLTextAreaElement>(null);
  const [recording, setRecording] = useState(false);
  const [notice, setNotice] = useState<{ kind: "warning" | "error"; text: string } | null>(null);

  const phase = greetingState(block, saved, text);
  const canRecord = block.voice && block.synthesiser;

  function insert(key: string) {
    const node = box.current;
    const token = `{{${key}}}`;
    const start = node?.selectionStart ?? text.length;
    const end = node?.selectionEnd ?? text.length;
    set("greeting", { text: (text.slice(0, start) + token + text.slice(end)).slice(0, MAX_GREETING_CHARS) });
    requestAnimationFrame(() => {
      node?.focus();
      node?.setSelectionRange(start + token.length, start + token.length);
    });
  }

  async function record() {
    setRecording(true);
    setNotice(null);
    try {
      const answer = await post<{ warning?: string }>("/v1/agent/greeting", { agent: agentId, text: saved });
      if (answer.warning) setNotice({ kind: "warning", text: answer.warning });
      onRecorded();
    } catch (err) {
      setNotice({ kind: "error", text: err instanceof ApiError ? err.message : "The dashboard could not reach the server." });
    } finally {
      setRecording(false);
    }
  }

  const busy = recording || (status.saving && phase === "pending");
  const pill = busy
    ? (["warm", "Recording…", "Turning your greeting into audio. Takes a few seconds."] as const)
    : {
        default: ["neutral", "Using the default", "Leave it empty and callers hear our standard greeting."] as const,
        pending: [
          "warm",
          "Not saved yet",
          text.trim() ? "Save your changes and it is recorded, so it plays the moment the call connects." : "Save your changes and callers hear our standard greeting again.",
        ] as const,
        live: ["warm", "Read out live", "Spoken live on each call, so there is a short pause before it starts."] as const,
        recorded: ["good", "Recorded", `Plays instantly when the agent picks up. Recorded ${block.audio ? when(block.audio.recordedAt) : ""}.`] as const,
        stale: ["warm", "Recording out of date", "The name or the voice changed since it was recorded. Callers hear it read live until you record it again."] as const,
      }[phase];

  return (
    <>
      <SettingCard flush>
        <div className="flex items-center justify-between gap-3 border-b border-line-soft bg-ground px-4 py-3">
          <span className="text-xs font-medium text-ink-soft">First thing callers hear</span>
          <div className="flex flex-wrap gap-1.5">
            {block.variables.map((variable) => (
              <button
                key={variable.key}
                type="button"
                onClick={() => insert(variable.key)}
                className="h-7 rounded-xs border border-line bg-paper px-2.5 text-xs font-medium text-ink hover:border-ink"
              >
                + {variable.label}
              </button>
            ))}
          </div>
        </div>
        <textarea
          ref={box}
          aria-label="Greeting"
          rows={3}
          maxLength={MAX_GREETING_CHARS}
          value={text}
          placeholder={`Leave empty to use the default: “${block.fallback}”`}
          onChange={(event) => set("greeting", { text: event.target.value })}
          className="block w-full resize-none bg-paper px-4 py-4 text-lg text-ink outline-none placeholder:text-ink-ghost focus-visible:shadow-none"
        />
        <div className="flex flex-wrap items-center gap-3.5 border-t border-line-soft bg-ground px-4 py-3">
          <StatusPill tone={pill[0]}>{pill[1]}</StatusPill>
          <span className="min-w-0 flex-1 text-xs text-ink-soft">{pill[2]}</span>
          {canRecord && !busy && (phase === "live" || phase === "stale") && (
            <Button type="button" variant="primary" className="h-8.5 rounded-full" onClick={() => void record()}>
              {phase === "stale" ? "Record again" : "Record it"}
            </Button>
          )}
        </div>
        {block.audio && phase !== "pending" && phase !== "default" && (
          <div className="border-t border-line-soft bg-ground px-4 pb-3">
            <RecordingPlayer
              key={block.audio.sha256}
              path={`/v1/agent/greeting/audio?agent=${encodeURIComponent(agentId)}&v=${block.audio.sha256}`}
              durationMs={block.audio.durationMs}
            />
          </div>
        )}
      </SettingCard>
      <CountTag className="-mt-3 justify-self-end self-end" n={text.length} max={MAX_GREETING_CHARS} />
      {notice && (
        <p
          role={notice.kind === "error" ? "alert" : "status"}
          className={notice.kind === "error" ? "rounded-sm border border-alert-line bg-alert-soft px-3 py-2.5 text-sm text-ink" : "rounded-sm border border-warm-line bg-warm-soft px-3 py-2.5 text-sm text-ink"}
        >
          {notice.kind === "error" && <b className="font-medium">Not recorded. </b>}
          <span className="text-ink-soft">{notice.text}</span>
        </p>
      )}
    </>
  );
}
